import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEyeSlash, faEye, faSync, faChevronDown, faChevronRight, faExclamationTriangle, faGlobe, faLock, faSearch } from '@fortawesome/free-solid-svg-icons';
import { Card, Form, Button, Alert, Table, Badge, Col, Row, Modal, InputGroup, ProgressBar, Pagination, Nav, Tab } from '@themesberg/react-bootstrap';

import api from '../../utils/api';


export default () => {
  const [stories, setStories] = useState([]);
  const [filteredStories, setFilteredStories] = useState([]);
  const [filterType, setFilterType] = useState(null);
  const [activeTab, setActiveTab] = useState('fake');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [fakeAccounts, setFakeAccounts] = useState([]);
  const [currentAuthorPage, setCurrentAuthorPage] = useState(1);
  const [authorSearchText, setAuthorSearchText] = useState("");
  const authorsPerPage = 12;

  const [expandedUsers, setExpandedUsers] = useState({});
  const EXPAND_STEP = 15;

  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showUserDeleteModal, setShowUserDeleteModal] = useState(false);
  const [deleteTargetUser, setDeleteTargetUser] = useState(null);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchStories = async () => {
      try {
        setLoading(true);
        const res = await api.get('/content/admin-list?type=story');
        if (isMounted) {
          const storiesList = res.data?.contents || [];
          setStories(storiesList);
          setFilteredStories(storiesList);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error loading stories:", err);
          setError("Hikayeler yüklenirken hata oluştu: " + err.message);
          setLoading(false);
        }
      }
    };
    fetchStories();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchFakeAccounts = async () => {
      try {
        const res = await api.get('/users?isFake=true&isApproved=true&limit=500');
        if (isMounted) {
          // Backend { success, users, total } zarfı döndürüyor
          const users = res.data?.users || res.data || [];
          const accounts = (Array.isArray(users) ? users : []).map(u => ({
            id: u.uid || u.id || u._id,
            name: u.name || u.displayName || u.firstName || 'Bilinmeyen'
          }));
          setFakeAccounts(accounts);
        }
      } catch (err) {
        console.error("Error loading fake accounts:", err);
      }
    };
    fetchFakeAccounts();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    const testAccountIds = new Set(fakeAccounts.map(acc => acc.id));

    let filtered;
    if (activeTab === 'fake') {
      filtered = stories.filter(story => {
        const authorId = story.authorId || story.userId;
        return testAccountIds.has(authorId) || story.authorIsApproved === true;
      });
    } else {
      filtered = stories.filter(story => {
        const authorId = story.authorId || story.userId;
        return !testAccountIds.has(authorId) && story.authorIsApproved !== true;
      });
    }

    // Zaman filtresi
    if (filterType) {
      const now = new Date();
      if (filterType === "last24Hours") {
        const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        filtered = filtered.filter(story => story.createdAt && story.createdAt >= yesterday);
      } else if (filterType === "last7Days") {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter(story => story.createdAt && story.createdAt >= sevenDaysAgo);
      }
    }

    setFilteredStories(filtered);
  }, [filterType, stories, fakeAccounts, activeTab]);

  // Filtre veya tab değiştiğinde sayfayı sıfırla
  useEffect(() => {
    setCurrentAuthorPage(1);
    setExpandedUsers({});
  }, [filterType, activeTab, authorSearchText]);

  // Hikayeleri kullanıcıya göre grupla
  const groupedStories = React.useMemo(() => {
    const groups = {};
    filteredStories.forEach(story => {
      const key = story.authorId || story.userId || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          userId: key,
          userName: story.authorName || 'Bilinmeyen',
          userAvatar: story.authorAvatar,
          stories: []
        };
      }
      groups[key].stories.push(story);
    });
    return Object.values(groups).sort((a, b) => b.stories.length - a.stories.length);
  }, [filteredStories]);

  // Arama filtresi uygulanmış yazarlar
  const searchedAuthors = React.useMemo(() => {
    if (!authorSearchText) return groupedStories;
    return groupedStories.filter(g => g.userName.toLowerCase().includes(authorSearchText.toLowerCase()));
  }, [groupedStories, authorSearchText]);

  // Sayfalanmış yazarlar
  const paginatedAuthors = React.useMemo(() => {
    const startIndex = (currentAuthorPage - 1) * authorsPerPage;
    return searchedAuthors.slice(startIndex, startIndex + authorsPerPage);
  }, [searchedAuthors, currentAuthorPage]);

  const totalAuthorPages = Math.ceil(searchedAuthors.length / authorsPerPage);

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString('tr-TR', {
      timeZone: 'Europe/Istanbul',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const handleDeleteStory = async (storyId) => {
    if (!window.confirm("Bu hikayeyi kalıcı olarak silmek istediğinizden emin misiniz?")) return;
    setActionLoading(storyId);
    try {
      await api.delete(`/content/admin-delete/${storyId}`);
      setStories(prev => prev.filter(s => s.id !== storyId));
      setFilteredStories(prev => prev.filter(s => s.id !== storyId));
      setSuccess("Hikaye başarıyla silindi.");
    } catch (err) {
      setError("Silme hatası: " + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleVisibility = async (story) => {
    const newVisibility = !(story.isVisibleToOthers !== false);
    setActionLoading(story.id);
    try {
      await api.post(`/content/admin-update/${story.id}`, { isVisibleToOthers: newVisibility });
      const updateState = (prev) => prev.map(s => s.id === story.id ? { ...s, isVisibleToOthers: newVisibility } : s);
      setStories(updateState);
      setFilteredStories(updateState);
      setSuccess(`Hikaye artık ${newVisibility ? 'herkese açık' : 'gizli'}.`);
    } catch (err) {
      setError("Güncelleme hatası: " + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleTogglePublicOverride = async (story) => {
    const newValue = !story.isPublicOverride;
    setActionLoading(story.id);
    try {
      await api.post(`/content/admin-update/${story.id}`, { isPublicOverride: newValue });
      const updateState = (prev) => prev.map(s => s.id === story.id ? { ...s, isPublicOverride: newValue } : s);
      setStories(updateState);
      setFilteredStories(updateState);
      setSuccess(newValue ? 'Hikaye herkese açıldı.' : 'Hikaye sadece premium üyelere açık.');
    } catch (err) {
      setError("Güncelleme hatası: " + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleRepublish = async (story) => {
    if (!window.confirm("Bu hikayeyi güncel tarihle yeniden yayınlamak istiyor musunuz?")) return;
    setActionLoading(story.id);
    try {
      const mediaUrl = story.mediaUrl || story.videoUrl || story.imageUrl;
      await api.post('/content/admin-create', {
        userId: story.authorId || story.userId,
        authorId: story.authorId || story.userId,
        authorName: story.authorName,
        authorAvatar: story.authorAvatar,
        type: 'story',
        mediaUrl,
        imageUrl: story.imageUrl || (!story.isVideo ? mediaUrl : null),
        videoUrl: story.videoUrl || (story.isVideo ? mediaUrl : null),
        mediaType: story.mediaType || (story.isVideo ? 'video' : 'image'),
        isVideo: !!story.isVideo,
        isApproved: true,
        isActive: true,
        isVisibleToOthers: true
      });
      setSuccess("Hikaye başarıyla yeniden yayınlandı.");
    } catch (err) {
      setError("Yeniden yayınlama hatası: " + (err.response?.data?.error || err.message));
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUserStories = async () => {
    if (!deleteTargetUser) return;
    const { userId, count } = deleteTargetUser;
    setBulkDeleteProgress({ current: 0, total: count });
    setShowUserDeleteModal(false);
    try {
      const userStories = filteredStories.filter(s => (s.authorId || s.userId) === userId);
      let deleted = 0;
      for (const st of userStories) {
        await api.delete(`/content/admin-delete/${st.id}`);
        deleted++;
        setBulkDeleteProgress({ current: deleted, total: count });
      }
      const ids = new Set(userStories.map(s => s.id));
      setStories(prev => prev.filter(s => !ids.has(s.id)));
      setFilteredStories(prev => prev.filter(s => !ids.has(s.id)));
      setSuccess(`${deleted} hikaye başarıyla silindi.`);
    } catch (err) {
      setError("Toplu silme hatası: " + (err.response?.data?.error || err.message));
    } finally {
      setBulkDeleteProgress(null);
      setDeleteTargetUser(null);
    }
  };

  const handleBulkDeleteAll = async () => {
    setShowBulkDeleteModal(false);
    const total = filteredStories.length;
    setBulkDeleteProgress({ current: 0, total });
    try {
      let deleted = 0;
      const ids = [];
      for (const st of filteredStories) {
        await api.delete(`/content/admin-delete/${st.id}`);
        ids.push(st.id);
        deleted++;
        setBulkDeleteProgress({ current: deleted, total });
      }
      const idSet = new Set(ids);
      setStories(prev => prev.filter(s => !idSet.has(s.id)));
      setFilteredStories(prev => prev.filter(s => !idSet.has(s.id)));
      setSuccess(`${deleted} hikaye tamamen silindi.`);
    } catch (err) {
      setError("Toplu silme hatası: " + (err.response?.data?.error || err.message));
    } finally {
      setBulkDeleteProgress(null);
    }
  };

  const toggleExpand = (userId) => {
    setExpandedUsers(prev => {
      const current = prev[userId] || 0;
      if (current === 0) {
        return { ...prev, [userId]: EXPAND_STEP };
      } else {
        return { ...prev, [userId]: 0 };
      }
    });
  };

  const showMore = (userId, totalCount) => {
    setExpandedUsers(prev => {
      const current = prev[userId] || 0;
      return { ...prev, [userId]: Math.min(current + EXPAND_STEP, totalCount) };
    });
  };

  const isMemberTab = activeTab === 'member';

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div className="d-block mb-4 mb-md-0">
          <h4>Hikayeler</h4>
          <p className="mb-0 text-muted">
            Uygulamadaki hikayeleri yönetin.
            {!loading && <span className="ms-2"><strong>{filteredStories.length}</strong> hikaye, <strong>{groupedStories.length}</strong> kullanıcı</span>}
          </p>
        </div>
        <div className="btn-toolbar mb-2 mb-md-0 gap-2">
          {filteredStories.length > 0 && (
            <Button variant="danger" size="sm" onClick={() => setShowBulkDeleteModal(true)} disabled={bulkDeleteProgress !== null}>
              <FontAwesomeIcon icon={faTrash} className="me-2" />
              Tümünü Sil ({filteredStories.length})
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess(false)} dismissible>{success}</Alert>}

      {bulkDeleteProgress && (
        <Alert variant="warning" className="mb-3">
          <div className="d-flex align-items-center mb-2">
            <FontAwesomeIcon icon={faTrash} className="me-2" spin />
            Siliniyor: {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
          </div>
          <ProgressBar
            now={(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}
            label={`${Math.round((bulkDeleteProgress.current / bulkDeleteProgress.total) * 100)}%`}
            variant="danger"
          />
        </Alert>
      )}

      {/* Tab bar: Fake / Üye */}
      <Nav variant="tabs" className="mb-3">
        <Nav.Item>
          <Nav.Link active={activeTab === 'fake'} onClick={() => setActiveTab('fake')}>
            <FontAwesomeIcon icon={faEye} className="me-2" />
            Fake Hesap Paylaşımları
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link active={activeTab === 'member'} onClick={() => setActiveTab('member')}>
            <FontAwesomeIcon icon={faLock} className="me-2" />
            Üyelerin Paylaşımı
            <Badge bg="info" className="ms-2">Premium</Badge>
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {isMemberTab && (
        <Alert variant="info" className="mb-3">
          <small>
            <strong>Not:</strong> Buradaki hikayeler sadece premium üyelere gösterilir.
            "Herkese Aç" butonuna basıldığında herkes görebilir.
          </small>
        </Alert>
      )}

      <Card border="light" className="shadow-sm">
        <Card.Body className="pt-0">
          <div className="table-settings mb-4">
            <Row className="align-items-center justify-content-between">
              <Col xs={8} md={4} className="mt-3">
                <Form.Select value={filterType || ""} onChange={(e) => setFilterType(e.target.value || null)}>
                  <option value="">Filtrele: Tümü</option>
                  <option value="last24Hours">Son 24 Saat</option>
                  <option value="last7Days">Son 7 Gün</option>
                </Form.Select>
              </Col>
              <Col xs={12} md={4} className="mt-3">
                <InputGroup>
                  <InputGroup.Text>
                    <FontAwesomeIcon icon={faSearch} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="İsim ile veya kullanıcı adı ile ara..."
                    value={authorSearchText}
                    onChange={(e) => setAuthorSearchText(e.target.value)}
                  />
                </InputGroup>
              </Col>
            </Row>
          </div>

          {loading ? (
            <div className="text-center py-5">Yükleniyor...</div>
          ) : paginatedAuthors.length === 0 ? (
            <div className="text-center py-5 text-muted">Henüz hikaye yok.</div>
          ) : (
            <>
              {paginatedAuthors.map((group) => {
                const visibleCount = expandedUsers[group.userId] || 0;
                const isOpen = visibleCount > 0;
                const visibleItems = group.stories.slice(0, visibleCount);
                const remaining = group.stories.length - visibleCount;

                return (
                  <div key={group.userId} className="mb-2">
                    {/* Kullanıcı Başlık Satırı */}
                    <div
                      className="d-flex align-items-center justify-content-between p-3"
                      style={{
                        background: isOpen ? '#e8edf5' : '#f8f9fa',
                        borderRadius: isOpen ? '8px 8px 0 0' : '8px',
                        cursor: 'pointer',
                        borderLeft: '4px solid',
                        borderLeftColor: isOpen ? '#4e73df' : '#adb5bd',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => toggleExpand(group.userId)}
                    >
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon
                          icon={isOpen ? faChevronDown : faChevronRight}
                          className="me-3 text-muted"
                          style={{ width: '12px' }}
                        />
                        <img
                          src={group.userAvatar || "/avatar.png"}
                          className="avatar rounded-circle me-3"
                          style={{ width: 40, height: 40, objectFit: 'cover' }}
                          alt=""
                        />
                        <div>
                          <span className="fw-bold text-dark">{group.userName}</span>
                          <Badge bg="secondary" className="ms-2">{group.stories.length} hikaye</Badge>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => {
                            setDeleteTargetUser({
                              userId: group.userId,
                              userName: group.userName,
                              count: group.stories.length
                            });
                            setShowUserDeleteModal(true);
                          }}
                          disabled={bulkDeleteProgress !== null}
                          title={`${group.userName} - Tüm hikayeleri sil`}
                        >
                          <FontAwesomeIcon icon={faTrash} className="me-1" />
                          Hepsini Sil
                        </Button>
                      </div>
                    </div>

                    {/* Hikaye Tablosu */}
                    {isOpen && (
                      <div style={{
                        background: '#ffffff',
                        borderRadius: '0 0 8px 8px',
                        border: '1px solid #dee2e6',
                        borderTop: 'none'
                      }}>
                        <Table hover className="user-table align-items-center mb-0" size="sm">
                          <thead>
                            <tr>
                              <th className="border-bottom" style={{ width: '80px', color: '#a0b4d0' }}>Görsel</th>
                              <th className="border-bottom" style={{ color: '#a0b4d0' }}>Tarih</th>
                              <th className="border-bottom" style={{ color: '#a0b4d0' }}>Durum</th>
                              {isMemberTab && <th className="border-bottom" style={{ color: '#a0b4d0' }}>Erişim</th>}
                              <th className="border-bottom" style={{ width: isMemberTab ? '200px' : '140px', color: '#a0b4d0' }}>İşlem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleItems.map(story => (
                              <tr key={story.id}>
                                <td>
                                  {story.imageUrl ? (
                                    <img
                                      src={story.imageUrl}
                                      alt="Hikaye"
                                      style={{ width: 50, height: 70, objectFit: 'cover', borderRadius: 6, cursor: 'pointer' }}
                                      onClick={() => window.open(story.imageUrl, '_blank')}
                                    />
                                  ) : (
                                    <span style={{ color: '#6c757d' }}>-</span>
                                  )}
                                </td>
                                <td style={{ color: '#343a40' }}><small>{formatDate(story.createdAt)}</small></td>
                                <td>
                                  {story.isVisibleToOthers === false ? (
                                    <Badge bg="warning">Gizli</Badge>
                                  ) : (
                                    <Badge bg="success">Yayında</Badge>
                                  )}
                                </td>
                                {isMemberTab && (
                                  <td>
                                    {story.isPublicOverride ? (
                                      <Badge bg="success"><FontAwesomeIcon icon={faGlobe} className="me-1" />Herkese Açık</Badge>
                                    ) : (
                                      <Badge bg="warning"><FontAwesomeIcon icon={faLock} className="me-1" />Sadece Premium</Badge>
                                    )}
                                  </td>
                                )}
                                <td>
                                  <div className="btn-group">
                                    {isMemberTab && (
                                      <Button
                                        variant={story.isPublicOverride ? 'success' : 'outline-warning'}
                                        size="sm"
                                        onClick={() => handleTogglePublicOverride(story)}
                                        disabled={actionLoading === story.id}
                                        title={story.isPublicOverride ? 'Premium\'a Kapat' : 'Herkese Aç'}
                                      >
                                        <FontAwesomeIcon icon={story.isPublicOverride ? faLock : faGlobe} className="me-1" />
                                        {story.isPublicOverride ? 'Kapat' : 'Herkese Aç'}
                                      </Button>
                                    )}
                                    {!isMemberTab && (
                                      <>
                                        <Button variant="outline-primary" size="sm" onClick={() => handleToggleVisibility(story)} disabled={actionLoading === story.id} title={story.isVisibleToOthers === false ? "Yayınla" : "Gizle"}>
                                          <FontAwesomeIcon icon={story.isVisibleToOthers === false ? faEye : faEyeSlash} />
                                        </Button>
                                        <Button variant="outline-info" size="sm" onClick={() => handleRepublish(story)} disabled={actionLoading === story.id} title="Yeniden Paylaş">
                                          <FontAwesomeIcon icon={faSync} />
                                        </Button>
                                      </>
                                    )}
                                    <Button variant="outline-danger" size="sm" onClick={() => handleDeleteStory(story.id)} disabled={actionLoading === story.id} title="Sil">
                                      <FontAwesomeIcon icon={faTrash} />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>

                        {/* +15 Daha Göster butonu */}
                        {remaining > 0 && (
                          <div className="text-center py-3">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => showMore(group.userId, group.stories.length)}
                            >
                              <FontAwesomeIcon icon={faChevronDown} className="me-2" />
                              +{Math.min(remaining, EXPAND_STEP)} daha göster
                              <span className="text-muted ms-2">({remaining} kalan)</span>
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Pagination UI */}
              {totalAuthorPages > 1 && (
                <div className="d-flex justify-content-between align-items-center p-3 mt-3 bg-light rounded shadow-sm">
                  <small className="text-muted">Toplam {searchedAuthors.length} yazar üzerinden {currentAuthorPage}. sayfa gösteriliyor</small>
                  <Pagination size="sm" className="mb-0">
                    <Pagination.Prev
                      onClick={() => setCurrentAuthorPage(p => Math.max(1, p - 1))}
                      disabled={currentAuthorPage === 1}
                    >
                      Geri
                    </Pagination.Prev>
                    {Array.from({ length: Math.min(5, totalAuthorPages) }, (_, i) => {
                      let pageNum;
                      if (totalAuthorPages <= 5) pageNum = i + 1;
                      else if (currentAuthorPage <= 3) pageNum = i + 1;
                      else if (currentAuthorPage >= totalAuthorPages - 2) pageNum = totalAuthorPages - 4 + i;
                      else pageNum = currentAuthorPage - 2 + i;

                      return (
                        <Pagination.Item
                          key={pageNum}
                          active={currentAuthorPage === pageNum}
                          onClick={() => setCurrentAuthorPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>
                      );
                    })}
                    <Pagination.Next
                      onClick={() => setCurrentAuthorPage(p => Math.min(totalAuthorPages, p + 1))}
                      disabled={currentAuthorPage === totalAuthorPages}
                    >
                      İleri
                    </Pagination.Next>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Tümünü Sil Modal */}
      <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
            Tüm Hikayeleri Sil
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            <strong>{filteredStories.length}</strong> adet hikayeyi kalıcı olarak silmek istediğinizden emin misiniz?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)}>İptal</Button>
          <Button variant="danger" onClick={handleBulkDeleteAll}>
            Evet, {filteredStories.length} Hikayeyi Sil
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Kullanıcının Tüm Hikayelerini Sil Modal */}
      <Modal show={showUserDeleteModal} onHide={() => setShowUserDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger me-2" />
            Kullanıcı Hikayelerini Sil
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            <strong>{deleteTargetUser?.userName}</strong> kullanıcısının <strong>{deleteTargetUser?.count}</strong> adet hikayesini kalıcı olarak silmek istediğinizden emin misiniz?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowUserDeleteModal(false); setDeleteTargetUser(null); }}>İptal</Button>
          <Button variant="danger" onClick={handleDeleteUserStories}>
            Evet, {deleteTargetUser?.count} Hikayeyi Sil
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
